import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWhatsNew } from '../api/whatsNew';
import type { WhatsNewNote } from '../types/api';
import { readSeen, unreadIds, writeSeen } from '../utils/whatsNewSeen';

interface WhatsNew {
  /** Every note this person may read, newest first. Empty means show no entry point. */
  notes: WhatsNewNote[];
  /** Notes published since the panel was last opened in this browser. */
  unread: WhatsNewNote[];
  /** Ids to mark "New" in the open panel: what was unread when it was opened. */
  newIds: ReadonlySet<string>;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * The What's new panel: its notes, which of them are unread, and whether it is open.
 *
 * Opening the panel marks everything seen, so the dots go at once, but the panel
 * keeps its "New" markers until it closes: they are the point of opening it.
 *
 * The request goes out once per page load. A failure leaves the notes empty, which
 * hides every entry point rather than showing a panel with nothing in it.
 */
export function useWhatsNew(eligible: boolean): WhatsNew {
  const [notes, setNotes] = useState<WhatsNewNote[]>([]);
  const [unreadSet, setUnreadSet] = useState<ReadonlySet<string>>(new Set());
  const [newIds, setNewIds] = useState<ReadonlySet<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const asked = useRef(false);

  useEffect(() => {
    if (!eligible || asked.current) return;
    asked.current = true;
    fetchWhatsNew()
      .then(({ notes: loaded }) => {
        if (loaded.length === 0) return;
        const seen = readSeen();
        const unread = new Set(unreadIds(loaded, seen));
        // A first visit: remember the older notes as seen, so they stay quiet on
        // the next visit too, while the recent ones wait to be opened.
        if (seen === null) writeSeen(loaded.filter((n) => !unread.has(n.id)).map((n) => n.id));
        setNotes(loaded);
        setUnreadSet(unread);
      })
      .catch(() => {
        // No notes is the same as no panel.
      });
  }, [eligible]);

  const open = useCallback(() => {
    setNewIds(unreadSet);
    setUnreadSet(new Set());
    writeSeen(notes.map((n) => n.id));
    setIsOpen(true);
  }, [notes, unreadSet]);

  const close = useCallback(() => setIsOpen(false), []);

  const unread = useMemo(() => notes.filter((n) => unreadSet.has(n.id)), [notes, unreadSet]);

  return { notes, unread, newIds, isOpen, open, close };
}
